import { useParams, Link } from "react-router-dom";
import { useEffect } from "react";
import { articles, categories } from "@/mocks/articles";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import useJsonLd from "@/hooks/useJsonLd";

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug) || categories[0];
  const categoryArticles = articles.filter((a) => a.categorySlug === slug);

  // Dynamic document title
  useEffect(() => {
    document.title = `${category.name} - 51黑料网 | 全网热门黑料爆料合集`;
  }, [category]);

  // Schema.org CollectionPage structured data
  useJsonLd({
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": `${category.name} - 51黑料网`,
    "description": category.description,
    "url": `http://bxubjgt.cn/category/${category.slug}`,
    "mainEntity": {
      "@type": "ItemList",
      "numberOfItems": categoryArticles.length,
      "itemListElement": categoryArticles.slice(0, 10).map((article, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "NewsArticle",
          "headline": article.title,
          "url": `http://bxubjgt.cn/article/${article.id}`,
          "datePublished": article.date,
          "image": article.image,
          "description": article.excerpt,
          "author": {
            "@type": "Person",
            "name": article.author
          },
          "publisher": {
            "@type": "Organization",
            "name": "51黑料网",
            "url": "http://bxubjgt.cn/"
          }
        }
      }))
    }
  });

  // Schema.org BreadcrumbList
  useJsonLd({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "首页",
        "item": "http://bxubjgt.cn/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": category.name,
        "item": `http://bxubjgt.cn/category/${category.slug}`
      }
    ]
  });

  const displayCategories = categories.length > 0 ? categories : [];

  return (
    <>
      <Navbar />

      <main>
        {/* Hero Banner */}
        <section
          className="relative mx-4 md:mx-6 mt-4 md:mt-6 rounded-[28px] overflow-hidden h-[320px] md:h-[420px] flex items-center justify-center"
          aria-label="分类Hero"
        >
          <img
            src="https://readdy.ai/api/search-image?query=Dark%20abstract%20artistic%20textured%20background%20with%20deep%20warm%20tones%2C%20crimson%20and%20charcoal%20gradient%2C%20editorial%20magazine%20spread%20aesthetic%2C%20mysterious%20atmospheric%2C%20no%20text%2C%20no%20people%2C%20luxury%20minimalist%20backdrop&width=1800&height=900&seq=category-hero-bg&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/25 to-black/55"></div>
          <div className="relative z-10 text-center w-full px-4">
            <span className="inline-block px-4 py-1.5 rounded-full border border-background-50/25 bg-background-50/10 text-background-50 text-xs md:text-sm font-medium whitespace-nowrap">
              {category.name}
            </span>
            <h1 className="mt-5 text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 leading-tight">
              {category.name}
            </h1>
            <p className="mt-4 text-sm md:text-base text-background-50/65 max-w-lg mx-auto leading-relaxed">
              {category.description}
            </p>
          </div>
        </section>

        {/* Category Index Grid */}
        <section className="mt-14 md:mt-18 px-4 md:px-6" aria-labelledby="category-index-heading">
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-1.5 rounded-full border border-foreground-200 text-foreground-600 text-xs font-medium whitespace-nowrap">
              全部分类
            </span>
            <h2 id="category-index-heading" className="mt-4 font-heading text-3xl md:text-4xl font-bold text-foreground-950">
              探索更多<br />黑料分类
            </h2>
            <p className="mt-3 text-sm text-foreground-500 max-w-md mx-auto">
              按分类浏览，快速找到你感兴趣的爆料内容
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 max-w-5xl mx-auto">
            {displayCategories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`group rounded-2xl border p-5 md:p-6 transition-all duration-300 cursor-pointer ${
                  cat.slug === slug
                    ? "bg-primary-500 border-primary-500"
                    : "bg-background-50 border-foreground-100/20 hover:bg-primary-500 hover:border-primary-500"
                }`}
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 transition-colors ${
                  cat.slug === slug ? "bg-background-50/20" : "bg-foreground-50 group-hover:bg-background-50/20"
                }`}>
                  <i className={`${cat.icon} text-lg ${cat.slug === slug ? "text-background-50" : "text-foreground-600 group-hover:text-background-50"}`}></i>
                </div>
                <h3 className={`font-heading text-lg font-semibold transition-colors ${
                  cat.slug === slug ? "text-background-50" : "text-foreground-900 group-hover:text-background-50"
                }`}>
                  {cat.name}
                </h3>
                <p className={`mt-1.5 text-sm leading-relaxed transition-colors ${
                  cat.slug === slug ? "text-background-50/70" : "text-foreground-500 group-hover:text-background-50/70"
                }`}>
                  {cat.description}
                </p>
                <span className={`inline-flex items-center gap-1 mt-3 text-xs font-medium transition-colors ${
                  cat.slug === slug ? "text-background-50" : "text-primary-500 group-hover:text-background-50"
                }`}>
                  浏览 {cat.count} 篇 <i className="ri-arrow-right-line text-xs w-3 h-3 flex items-center justify-center"></i>
                </span>
              </Link>
            ))}
          </div>
        </section>

        {/* Category Articles Grid */}
        <section className="mt-16 md:mt-20 px-4 md:px-6" aria-labelledby="category-articles-heading">
          <div className="mb-10">
            <h2
              id="category-articles-heading"
              className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 leading-tight"
            >
              {category.name}<br />全部内容
            </h2>
          </div>

          {categoryArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {categoryArticles.map((article) => (
                <article key={article.id} className="group cursor-pointer rounded-2xl border border-foreground-100/20 overflow-hidden bg-background-50 transition-all duration-300 hover:border-primary-200/50">
                  <Link to={`/article/${article.id}`} className="block">
                    <div className="relative w-full h-48 md:h-52 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        title={`${article.title} - 51黑料网`}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                  </Link>
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="text-xs text-foreground-400 whitespace-nowrap">{article.date}</span>
                      <span className="text-xs text-foreground-400 whitespace-nowrap">
                        <i className="ri-eye-line mr-0.5 w-3 h-3 inline-flex items-center justify-center"></i>
                        {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
                      </span>
                    </div>
                    <Link to={`/article/${article.id}`}>
                      <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h3>
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-foreground-50 flex items-center justify-center">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500 text-sm">该分类暂无内容，敬请期待</p>
            </div>
          )}
        </section>
      </main>

      <Footer />
    </>
  );
}
